import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "./supabase";

export const PickAlert = () => {
  const [latestPick, setLatestPick] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);

  const leagueLogoUrl =
    "https://dpsrbqvcukoomuupjfdm.supabase.co/storage/v1/object/public/draft%20bucket/Empire%20League%20Logo.png";

  useEffect(() => {
    const channel = supabase
      .channel("realtime-picks")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "picks" },
        async (payload: any) => {
          const { data, error } = await supabase
            .from("picks")
            .select(
              `
              player:players(name, position, hometown),
              team:teams(team_name, primary_color)
            `
            )
            .eq("id", payload.new.id)
            .single();

          if (data && !error) {
            setLatestPick(data);
            setIsVisible(true);
            setTimeout(() => setIsVisible(false), 8000);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Helper to split First and Last Name
  const getSplitName = (fullName: string) => {
    if (!fullName) return { first: "", last: "" };
    const parts = fullName.trim().split(" ");
    if (parts.length === 1) return { first: "", last: parts[0] };
    const last = parts.pop() || "";
    const first = parts.join(" ");
    return { first, last };
  };

  const nameParts = latestPick
    ? getSplitName(latestPick.player.name)
    : { first: "", last: "" };

  return (
    <AnimatePresence>
      {isVisible && latestPick && (
        <motion.div
          key="pick-alert"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="pick-alert-overlay"
        >
          <div className="alert-scanlines"></div>
          <div className="alert-vignette"></div>

          <div className="alert-content-wrapper">
            <motion.img
              src={leagueLogoUrl}
              alt="Empire League"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="alert-league-logo"
            />

            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="alert-status-badge"
            >
              <div className="badge-glow"></div>
              <span>THE PICK IS IN</span>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="alert-team-announcement"
              style={{ color: latestPick.team.primary_color }}
            >
              {latestPick.team.team_name}
            </motion.div>

            {/* STACKED NAME SECTION */}
            <div className="alert-name-stack">
              {nameParts.first && (
                <motion.span
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 0.8 }}
                  transition={{ delay: 0.9, duration: 0.5 }}
                  className="alert-first-name"
                >
                  {nameParts.first}
                </motion.span>
              )}
              <motion.h1
                initial={{
                  letterSpacing: "15px",
                  opacity: 0,
                  filter: "blur(15px)",
                }}
                animate={{
                  letterSpacing: "2px",
                  opacity: 1,
                  filter: "blur(0px)",
                }}
                transition={{ delay: 1, duration: 0.8 }}
                className="alert-last-name"
              >
                {nameParts.last}
              </motion.h1>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="alert-player-details"
            >
              <span className="detail-pos">{latestPick.player.position}</span>
              <span className="detail-divider"></span>
              <span className="detail-home">
                {latestPick.player.hometown || "Unknown"}
              </span>
            </motion.div>

            <motion.div
              className="alert-bottom-bar"
              initial={{ width: 0 }}
              animate={{ width: "40%" }}
              transition={{ delay: 1.2, duration: 1 }}
              style={{ background: latestPick.team.primary_color }}
            />
          </div>

          <style>{`
            .pick-alert-overlay {
              position: fixed;
              top: 0; left: 0;
              width: 100vw; height: 100vh;
              background: #0a0a0a;
              z-index: 10000;
              display: flex;
              justify-content: center;
              align-items: center;
              overflow: hidden;
              font-family: 'Oswald', sans-serif;
              border: 20px solid #22565a;
            }

            .alert-scanlines {
              position: absolute; inset: 0;
              background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), 
                          linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06));
              background-size: 100% 4px, 3px 100%;
              pointer-events: none; z-index: 1;
            }

            .alert-vignette {
              position: absolute; inset: 0;
              background: radial-gradient(circle, transparent 20%, black 100%);
              z-index: 2;
            }

            .alert-content-wrapper {
              position: relative;
              z-index: 10;
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
              width: 100%;
            }

            /* --- STACKED NAME STYLING --- */
            .alert-name-stack {
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 90%;
              max-width: 1400px;
              margin: 10px 0;
            }

            .alert-first-name {
              font-size: 2.2rem;
              color: #f9eeda;
              text-transform: uppercase;
              font-weight: 400;
              letter-spacing: 8px;
              margin-bottom: -10px; /* Pulls last name closer */
              opacity: 0.8;
            }

            .alert-last-name {
              font-size: clamp(4rem, 9vw, 8.5rem);
              color: #f9eeda;
              text-transform: uppercase;
              line-height: 0.95;
              font-weight: 900;
              text-shadow: 0 0 40px rgba(249, 238, 218, 0.15);
              margin: 0;
            }

            .alert-league-logo { height: 110px; margin-bottom: 15px; }

            .alert-status-badge {
              position: relative; background: #22565a; color: #f9eeda;
              padding: 6px 30px; font-size: 1.3rem; font-weight: 800;
              letter-spacing: 5px; border-radius: 2px; margin-bottom: 20px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.5); overflow: hidden;
            }

            .badge-glow {
              position: absolute; top: 0; left: -100%; width: 100%; height: 100%;
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
              animation: shimmer 3s infinite;
            }

            .alert-team-announcement {
              font-size: 2rem; font-weight: 700; text-transform: uppercase;
              letter-spacing: 2px; text-shadow: 2px 2px 8px rgba(0,0,0,1);
            }

            .alert-player-details {
              display: flex; align-items: center; gap: 20px;
              margin-top: 20px; font-size: 1.8rem; color: #aaa;
              text-transform: uppercase;
            }

            .detail-divider { width: 2px; height: 20px; background: #444; }
            .detail-home { color: #22565a; font-weight: 700; }

            .alert-bottom-bar { height: 4px; margin-top: 40px; box-shadow: 0 0 15px currentColor; }

            @keyframes shimmer { 100% { left: 100%; } }

            @media (max-width: 1024px) {
              .alert-last-name { font-size: clamp(3rem, 12vw, 5rem); }
              .alert-first-name { font-size: 1.4rem; letter-spacing: 4px; }
            }

            @media (max-width: 768px) {
              .pick-alert-overlay { border: 10px solid #22565a; }
              .alert-league-logo { height: 70px; }
              .alert-status-badge { font-size: 1rem; padding: 4px 15px; letter-spacing: 3px; }
              .alert-team-announcement { font-size: 1.3rem; }
              .alert-first-name { font-size: 1.1rem; letter-spacing: 3px; margin-bottom: -5px; }
              .alert-last-name { font-size: clamp(2.5rem, 15vw, 4rem); }
              .alert-player-details { font-size: 1.1rem; gap: 10px; margin-top: 15px; }
              .detail-divider { height: 12px; }
              .alert-bottom-bar { margin-top: 25px; width: 60% !important; }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
